import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => navigate(user ? '/dashboard' : '/register');

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ fontFamily: "'Manrope', sans-serif", background: 'linear-gradient(180deg, #0B0F1A 0%, #0E1320 50%, #0B0F1A 100%)' }}>
      {/* Keyframes */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .fade-1 { animation: fadeUp 0.6s ease-out 0.3s both; }
        .fade-2 { animation: fadeUp 0.6s ease-out 0.5s both; }
        .fade-3 { animation: fadeUp 0.6s ease-out 0.7s both; }
        .fade-4 { animation: fadeUp 0.6s ease-out 0.9s both; }
        .fade-5 { animation: fadeUp 0.6s ease-out 1.1s both; }
      `}</style>

      {/* Film grain */}
      <div aria-hidden="true" className="fixed inset-0 z-50 pointer-events-none opacity-[0.03] mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '150px' }} />

      {/* Warm vignette */}
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(214,168,92,0.06) 0%, transparent 60%)' }} />

      {/* Nav */}
      <header className="fixed top-0 w-full z-40 backdrop-blur-xl border-b border-[#F5EDE4]/[0.06]" style={{ backgroundColor: 'rgba(11,15,26,0.7)' }}>
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <span className="text-base font-medium tracking-tight text-[#F5EDE4]" data-testid="nav-brand">Scriptwriter Pro</span>
          <button
            onClick={() => navigate(user ? '/dashboard' : '/login')}
            className="text-xs tracking-[0.15em] uppercase text-[#F5EDE4]/50 hover:text-[#D6A85C] transition-colors duration-300"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
            data-testid="landing-login-btn"
          >
            {user ? 'Dashboard' : 'Sign in'}
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 pt-20 min-h-[90vh] relative z-10">
        <div className="flex flex-col items-center text-center max-w-3xl gap-7">
          <h1
            className="fade-1 text-5xl sm:text-6xl md:text-7xl font-medium tracking-tighter leading-[1.08] text-[#F5EDE4]"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="hero-heading"
          >
            Write Your Film<br />in Minutes.
          </h1>

          <p className="fade-2 text-base sm:text-lg leading-relaxed text-[#F5EDE4]/60 max-w-xl">
            Let Scriptwriter Pro create your first draft — so you can focus on building your world and telling your story from day one.
          </p>

          <p className="fade-3 text-sm sm:text-base text-[#D6A85C]/70 italic font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your first screenplay is on us.
          </p>

          <button
            onClick={handleStart}
            className="fade-4 mt-4 inline-flex items-center gap-2.5 px-8 py-4 text-sm font-medium transition-all duration-300 hover:scale-[1.02]"
            style={{ backgroundColor: '#D6A85C', color: '#0B0F1A', boxShadow: '0 0 0 0 rgba(214,168,92,0)' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 24px 4px rgba(214,168,92,0.25)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 0 0 rgba(214,168,92,0)'}
            data-testid="get-started-btn"
          >
            Start Writing Your Film
          </button>

          <p className="fade-5 text-xs text-[#F5EDE4]/30" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            Generates a structured screenplay draft in minutes
          </p>
        </div>

        {/* Value Section */}
        <div className="mt-32 w-full max-w-screen-md">
          <h2
            className="text-2xl sm:text-3xl font-medium tracking-tight text-[#F5EDE4] text-center mb-14"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            From Idea to Script
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: '01', title: 'Structure your story', desc: 'We shape your idea into a clear three-act foundation' },
              { icon: '02', title: 'Generate your draft', desc: 'A complete screenplay appears in minutes' },
              { icon: '03', title: 'Refine your vision', desc: 'Add notes, rewrite with feedback, and make it yours' },
            ].map(({ icon, title, desc }) => (
              <div key={icon} className="relative p-8 border border-[#F5EDE4]/[0.06] group hover:border-[#D6A85C]/20 transition-all duration-300" style={{ backgroundColor: 'rgba(245,237,228,0.02)' }}>
                <span className="text-3xl font-light text-[#D6A85C]/20 block mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{icon}</span>
                <h3 className="text-sm font-medium text-[#F5EDE4] mb-2">{title}</h3>
                <p className="text-sm text-[#F5EDE4]/40 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-[#F5EDE4]/40 mt-12 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            We handle the structure. You bring the vision.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-12 border-t border-[#F5EDE4]/[0.06] mt-24">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs text-[#F5EDE4]/30" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Scriptwriter Pro</p>
        </div>
      </footer>
    </div>
  );
}
